import { useState, useEffect } from "react";
import {
  Flex,
  Heading,
  Alert,
  AlertIcon,
  Button,
  Text,
  Box,
  Input,
} from "@chakra-ui/react";
import axios from "axios";
import styled from "styled-components";
import { FormInput, Logo } from "../components";
import { useAppContext } from "../context/appContext/appContext";

// A column only ever needs to be as wide as its longest unbreakable word
// (text wraps between words, never within one), so size columns off the
// longest single token in the header or data rather than full string
// length - that way a single-word header like "WINS" always gets enough
// room to stay on one line, while multi-word headers are free to wrap.
// Width is capped per column so one long outlier value can't crowd out
// the rest of the table.
const maxTokenLength = (value) => {
  const str = String(value ?? "").trim();
  if (!str) return 0;
  return Math.max(...str.split(/\s+/).map((token) => token.length));
};

const getColumnWidths = (headers, rows) => {
  const weights = headers.map((h) => {
    const headerToken = maxTokenLength(h);
    const maxDataToken = rows.reduce((max, row) => {
      const token = maxTokenLength(row[h]);
      return token > max ? token : max;
    }, 0);
    return Math.min(Math.max(headerToken, maxDataToken, 3), 14);
  });
  const total = weights.reduce((sum, w) => sum + w, 0);
  return weights.map((w) => (w / total) * 100);
};

// Papa Parse dedupes blank/duplicate header cells by appending "_<n>" to
// the original name (e.g. two blank headers become "" and "_1"). When the
// original name was blank, the result is just "_<n>" with nothing in front
// of the underscore - that's the signature of an auto-deduped blank header,
// as opposed to a real duplicate like "WINS" -> "WINS_1". Render those as
// blank instead of leaking the raw parser-generated key to the user.
const BLANK_DEDUPED_HEADER = /^_\d+$/;
const displayHeaderLabel = (h) => (BLANK_DEDUPED_HEADER.test(h) ? "" : h);

const Standings = () => {
  const { user } = useAppContext();

  const [checking, setChecking] = useState(true);
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");

  const [file, setFile] = useState(null);
  const [working, setWorking] = useState(false);
  const [adminMsg, setAdminMsg] = useState("");

  const fetchStandings = async () => {
    setLoadError("");
    try {
      const { data } = await axios.get("/api/v1/standings/data");
      setData(data);
      setUnlocked(true);
    } catch (error) {
      if (error.response?.status === 401) {
        setUnlocked(false);
      } else {
        // Already unlocked, but the data itself failed to load (sheet
        // unreachable/misconfigured) - keep the form gone, show the error.
        setUnlocked(true);
        setLoadError(
          error.response?.data?.msg || "Unable to load standings right now.",
        );
      }
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    fetchStandings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitPassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    try {
      await axios.post("/api/v1/standings/unlock", { password });
      setPassword("");
      setChecking(true);
      fetchStandings();
    } catch (error) {
      setPasswordError(error.response?.data?.msg || "Incorrect password");
    }
  };

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setWorking(true);
    setAdminMsg("");
    const formData = new FormData();
    formData.append("pdf", file);
    try {
      await axios.post("/api/v1/standings/pdf", formData);
      setAdminMsg("PDF uploaded - now showing as the active standings.");
      setFile(null);
      fetchStandings();
    } catch (error) {
      setAdminMsg(error.response?.data?.msg || "Upload failed");
    } finally {
      setWorking(false);
    }
  };

  const revertToSheet = async () => {
    setWorking(true);
    setAdminMsg("");
    try {
      await axios.delete("/api/v1/standings/pdf");
      setAdminMsg("Reverted to the live sheet.");
      fetchStandings();
    } catch (error) {
      setAdminMsg(error.response?.data?.msg || "Failed to revert");
    } finally {
      setWorking(false);
    }
  };

  if (checking) {
    return (
      <Flex justify="center" align="center" minH="50vh">
        <Text color="headline.400">Loading...</Text>
      </Flex>
    );
  }

  if (!unlocked) {
    return (
      <Flex justify="center" align="center" mt={10}>
        <Wrapper>
          <form className="form" onSubmit={submitPassword}>
            <Logo
              image={"apple-touch-icon.png"}
              size={"50px"}
              color={"#63aab5"}
            />
            <Heading as="h2" size="lg" color="headline.400" fontStyle="italic">
              Standings
            </Heading>
            <Text textAlign="center">
              This page is password-protected. Enter the league password to view
              standings.
            </Text>
            {passwordError && (
              <Alert status="error">
                <AlertIcon />
                {passwordError}
              </Alert>
            )}
            <FormInput
              inputType="password"
              name="password"
              value={password}
              labelText="Password"
              handleChange={(e) => setPassword(e.target.value)}
            />
            <button className="form-btn" bg="headline.400" color="white">
              Unlock
            </button>
          </form>
        </Wrapper>
      </Flex>
    );
  }

  return (
    <Flex direction="column" align="center" gap="20px" p={{ base: 4, md: 8 }}>
      <Heading as="h2" size="lg" color="headline.400" fontStyle="italic">
        Farms CC Standings 2026-2027
      </Heading>

      {user?.admin && (
        <Box
          border="1px solid #63aab5"
          borderRadius="8px"
          p={4}
          w="100%"
          maxW="600px"
        >
          <Heading size="sm" mb={3} color="headline.400">
            Admin: Standings Override
          </Heading>
          <form onSubmit={submitUpload}>
            <Flex gap={3} align="center" wrap="wrap">
              <Input
                type="file"
                accept="application/pdf"
                onChange={(e) => setFile(e.target.files[0] || null)}
                w="auto"
              />
              <Button
                type="submit"
                isLoading={working}
                isDisabled={!file}
                bg="headline.400"
                color="white"
              >
                Upload PDF
              </Button>
              {data?.type === "pdf" && (
                <Button
                  onClick={revertToSheet}
                  isLoading={working}
                  variant="outline"
                >
                  Revert to Live Sheet
                </Button>
              )}
            </Flex>
          </form>
          {adminMsg && (
            <Text mt={2} fontSize="sm">
              {adminMsg}
            </Text>
          )}
        </Box>
      )}

      {loadError && (
        <Alert status="error" maxW="600px">
          <AlertIcon />
          {loadError}
        </Alert>
      )}

      {data?.type === "pdf" && (
        <Box w="100%" maxW="900px">
          <Text mb={2} fontSize="sm" color="gray.600">
            Showing uploaded PDF standings
            {data.uploadedAt
              ? ` (uploaded ${new Date(data.uploadedAt).toLocaleDateString()})`
              : ""}
            .
          </Text>
          <embed
            src="/api/v1/standings/pdf"
            type="application/pdf"
            width="100%"
            height="800px"
          />
          <Text mt={2}>
            <a
              href="/api/v1/standings/pdf"
              target="_blank"
              rel="noreferrer"
              style={{ color: "#63aab5" }}
            >
              Download PDF
            </a>
          </Text>
        </Box>
      )}

      {data?.type === "sheet" &&
        (data.rows.length === 0 ? (
          <Text>No standings available yet.</Text>
        ) : (
          <StandingsTableWrapper>
            <table>
              <colgroup>
                {getColumnWidths(data.headers, data.rows).map((width, i) => (
                  <col key={i} style={{ width: `${width}%` }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {data.headers.map((h) => (
                    <th key={h}>{displayHeaderLabel(h)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, i) => (
                  <tr key={i}>
                    {data.headers.map((h) => (
                      <td key={h}>{row[h]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </StandingsTableWrapper>
        ))}
    </Flex>
  );
};
export default Standings;

const Wrapper = styled.section`
  padding: 0 16px;
  box-sizing: border-box;

  .form {
    min-height: 300px;
    background-color: white;
    border: 3px solid #63aab5;
    margin: 0 auto;
    width: min(90vw, 500px);
    display: flex;
    box-sizing: border-box;
    align-items: center;
    border-radius: 5px;
    flex-direction: column;
    padding: clamp(16px, 5vw, 20px);
    gap: 20px;
  }

  .form-btn {
    width: min(180px, 100%);
    margin: 5px;
    margin-top: 10px;
    cursor: pointer;
    border-radius: 5px;
    color: white;
    font-weight: bold;
    background-color: #63aab5;
    border: none;
    padding: 5px;
  }
`;

const StandingsTableWrapper = styled.div`
  width: 100%;
  max-width: 900px;
  box-sizing: border-box;
  overflow: hidden;
  border: 1px solid #63aab5;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  background: white;

  table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
  }

  th,
  td {
    box-sizing: border-box;
    text-align: center;
    vertical-align: middle;
    padding: clamp(4px, 1.6vw, 14px) clamp(2px, 1vw, 12px);
    font-size: clamp(0.62rem, 2.2vw, 0.95rem);
    line-height: 1.25;
    word-break: normal;
    overflow-wrap: break-word;
    hyphens: auto;
  }

  th {
    background-color: #63aab5;
    color: white;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  td {
    color: #2d3748;
    border-top: 1px solid #e2e8f0;
  }

  tbody tr:nth-of-type(even) {
    background-color: #f7fafc;
  }

  @media (max-width: 768px) {
    border-radius: 8px;
  }

  @media (max-width: 430px) {
    border-radius: 6px;
    border-width: 1px;
  }
`;
